import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { cx, styled } from "simplydo/react/styled";
import { Popup } from "simplydo/react/ui";
import type { DropdownProps } from "simplydo/react/ui";

import { getStoredGroupVisits } from "utils/groupVisits";

import type {
  HoverLinkDropdownProps,
  NavChallenge,
  NavDropdownItem,
  NavDropdownType,
  NavDropdownUser,
  NavGroup,
  NavIdea,
} from "./types";

const asTimestamp = (value?: string | null) => {
  if (!value) return 0;
  const parsed = new Date(value ?? "").getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getGroupFallbackTimestamp = (group: NavGroup) =>
  asTimestamp(group?.activityInfo?.lastActivityAt) ||
  asTimestamp(group?.updatedAt) ||
  asTimestamp(group?.createdAt);

const getDropdownItems = (
  user: NavDropdownUser | null,
  type?: NavDropdownType,
): NavDropdownItem[] => {
  if (!user || !type) {
    return [];
  }
  if (type === "groups") {
    const groups = user.groups;
    if (!groups) return [];
    const groupVisits = getStoredGroupVisits(
      user?._id,
      user?.ownerOrganisation?._id,
    );
    const sortedGroups = [...groups].sort((a, b) => {
      const bVisitAt = asTimestamp(groupVisits[b._id]);
      const aVisitAt = asTimestamp(groupVisits[a._id]);
      if (bVisitAt !== aVisitAt) return bVisitAt - aVisitAt;

      const bFallback = getGroupFallbackTimestamp(b);
      const aFallback = getGroupFallbackTimestamp(a);
      if (bFallback !== aFallback) return bFallback - aFallback;

      return (a.name || "").localeCompare(b.name || "");
    });
    return sortedGroups.map((g) => ({
      _id: g._id,
      link: `/groups/${g._id}`,
      title: g.name,
    }));
  }
  if (type === "challenges") {
    if (!user.challenges) return [];
    return user.challenges.map((c: NavChallenge) => ({
      _id: c._id,
      link: `/challenges/${c._id}`,
      title: c.name,
    }));
  }
  if (type === "ideas") {
    if (!user.ideas) return [];
    return user.ideas.map((i: NavIdea) => ({
      _id: i._id,
      link: `/ideas/${i._id}`,
      title: i.name,
    }));
  }
  return [];
};

export const HoverLink = styled.div`
  padding: 7.5px 15px;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: black;

  &:hover {
    color: black;
  }
  &:first-of-type {
    border-top-left-radius: 2.5px;
    border-top-right-radius: 2.5px;
  }
  &:last-of-type {
    border-bottom-left-radius: 2.5px;
    border-bottom-right-radius: 2.5px;
  }
  a,
  span {
    color: black;
    display: block;
    overflow: hidden;
  }
  &.is-active {
    cursor: default;
    background-color: ${({ theme }) => theme.accent};
    span {
      color: ${({ theme }) => (theme.shouldBeWhiteOnAccent ? "#fff" : "#333")};
    }
  }
  &:not(.is-active):hover {
    background-color: #e9ebee;
    a,
    span {
      color: black;
    }
  }
`;

const HoverLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: none;
  > span {
    padding: 10px 15px 7.5px;
    font-size: 1.1em;
    font-weight: bold;
  }
`;

const LinkContainer = styled.div``;

const isActiveRoute = (link: string) => window.location.pathname === link;

const HoverLinkDropdown = ({
  user,
  type,
  title,
  trigger,
  content,
}: HoverLinkDropdownProps) => {
  const dropdownItems = getDropdownItems(user, type);
  if (!(content || dropdownItems?.length)) return <>{trigger}</>;
  return (
    <Popup
      on="hover"
      hoverable
      trigger={<LinkContainer>{trigger}</LinkContainer>}
      position="bottom center"
      style={{ padding: 0 }}
      offset={[0, -1]}
    >
      <HoverLinkContainer>
        {title ?
          <span>{title}</span>
        : null}
        {content ??
          dropdownItems.slice(0, 6).map((item) => (
            <HoverLink
              key={item._id}
              as={Link}
              to={item.link}
              className={cx(isActiveRoute(item.link) && "is-active")}
            >
              {isActiveRoute(item.link) ?
                <span>{item.title}</span>
              : <Link to={item.link}>{item.title}</Link>}
            </HoverLink>
          ))}
      </HoverLinkContainer>
    </Popup>
  );
};

const mapStateToProps = (state: { user: NavDropdownUser | null }) => ({
  user: state.user,
});

export default connect(mapStateToProps)(HoverLinkDropdown);
